import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Photographer } from '../../../marketplace/entities/photographer/photographer.entity';
import {
  BlogArticle,
  BlogArticleDetail,
} from '../../../blog/entities/article.entity';
import { forkJoin } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {environment} from '../../../../environments/environment';

type LangCode = 'fr' | 'en';
type CityKey = 'paris' | 'marseille' | 'lyon' | 'bordeaux' | 'toulouse' | 'lille';

const ALLOWED_CITIES: CityKey[] = ['paris', 'marseille', 'lyon', 'bordeaux', 'toulouse', 'lille'];
const CITY_LABEL: Record<CityKey, string> = {
  paris: 'Paris',
  marseille: 'Marseille',
  lyon: 'Lyon',
  bordeaux: 'Bordeaux',
  toulouse: 'Toulouse',
  lille: 'Lille',
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private readonly titleService: Title,
    private readonly metaService: Meta,
    private readonly transloco: TranslocoService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Inject(DOCUMENT) private readonly doc: Document,
  ) {}

  /**
   * SEO for the marketplace (query-params based).
   * - Async i18n to avoid raw keys in <title>/<meta>
   * - OG image per city & language (assets/images/seo/marketplace/cities/og-{city}.{lang}.png)
   * - Canonical, robots, JSON-LD (CollectionPage + Breadcrumb + Aggregate)
   * - With a safe fallback reading city/tags from URL if called too early
   */
  public setupSEOForMarketplace(
    city: string | null = null,
    tags: string[] = [],
    opts?: { resultsCount?: number },
  ): void {
    this.clearSeoTags();
    this.clearJsonLd(['sf-collection', 'sf-breadcrumbs', 'sf-aggregate', 'sf-itemlist']);

    // -------- Resolve effective filters (fallback from URL if needed) --------
    const urlFallback = this.readUrlParamsFallback();
    const effCity = (city ?? urlFallback.city ?? '').trim().toLowerCase();
    const effTags = (tags?.length ? tags : urlFallback.tags).map(t => t.trim().toLowerCase());

    // Normalize for indexing vs display
    const cRaw = effCity || '';
    const cForIndex = (ALLOWED_CITIES.includes(cRaw as CityKey) ? (cRaw as CityKey) : null) as CityKey | null;
    const cForOg = (cRaw || null) as CityKey | null; // allow OG image even if not indexable
    const t = (effTags[0] || null);

    const lang = this.getActiveLang();
    const ogLocale = lang === 'fr' ? 'fr_FR' : 'en_US';

    const niceCity = cForOg ? CITY_LABEL[cForOg] : null;
    const hasCity = !!cForOg;
    const hasTag = !!t;

    // -------- Titles / Descriptions (async i18n with fallbacks) --------
    const fallbackH1 =
      hasCity && hasTag ? `Photographes ${t} à ${niceCity}` :
        hasCity ? `Photographes à ${niceCity}` :
          hasTag ? `Photographes ${t}` :
            'Trouvez un photographe près de chez vous';

    const titleKey =
      hasCity && hasTag ? 'seo.marketplace.title_city_tag' :
        hasCity ? 'seo.marketplace.title_city' :
          hasTag ? 'seo.marketplace.title_tag' :
            'seo.marketplace.title';

    const descKey =
      hasCity && hasTag ? 'seo.marketplace.description_city_tag' :
        hasCity ? 'seo.marketplace.description_city' :
          hasTag ? 'seo.marketplace.description_tag' :
            'seo.marketplace.description';

    const titleParams = { city: niceCity ?? '', tag: t ?? '' };
    const descParams = { city: niceCity ?? '', tag: t ?? '' };

    const title$ = this.tOnce(titleKey, titleParams, lang, `${fallbackH1} | ShutterFlow`);
    const desc$ = this.tOnce(
      descKey,
      descParams,
      lang,
      hasCity && hasTag
        ? `Réservez un photographe ${t} à ${niceCity}. Disponibilités, prix, paiement en ligne.`
        : hasCity
          ? `Les meilleurs photographes à ${niceCity} : mariage, portrait, pro... Réservez en ligne en 1 minute.`
          : hasTag
            ? `Trouvez un photographe ${t}. Comparez les offres et réservez en ligne.`
            : 'Comparez des photographes, voyez les prix et réservez en ligne.'
    );

    forkJoin([title$, desc$]).pipe(take(1)).subscribe(([pageTitle, desc]) => {
      // Canonical keeps normalized query order
      const canonicalUrl = this.buildCanonicalWithParams(cForOg, effTags);

      // Robots: index only strong pages (whitelisted city) or the generic listing
      const indexable = (!!cForIndex) || (!hasCity && !hasTag);
      this.metaService.updateTag({ name: 'robots', content: indexable ? 'index,follow' : 'noindex,follow' });

      // Title / Description
      this.titleService.setTitle(pageTitle);
      this.metaService.updateTag({ name: 'description', content: desc });

      // Canonical + URL
      this.setCanonicalLink(canonicalUrl);
      this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });

      // OG / Twitter
      const ogImage = this.buildOgCityImage(cForOg, lang);
      this.metaService.updateTag({ property: 'og:type', content: 'website' });
      this.metaService.updateTag({ property: 'og:title', content: pageTitle });
      this.metaService.updateTag({ property: 'og:description', content: desc });
      this.metaService.updateTag({ property: 'og:image', content: ogImage });
      this.metaService.updateTag({ property: 'og:image:width', content: '1200' });
      this.metaService.updateTag({ property: 'og:image:height', content: '630' });
      this.metaService.updateTag({ property: 'og:image:alt', content: fallbackH1 });
      this.metaService.updateTag({ property: 'og:locale', content: ogLocale });
      this.metaService.updateTag({ property: 'og:site_name', content: 'ShutterFlow' });
      this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.metaService.updateTag({ name: 'twitter:image', content: ogImage });

      // JSON-LD: CollectionPage
      this.upsertJsonLd('sf-collection', {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: fallbackH1,
        description: desc,
        url: canonicalUrl,
        image: ogImage,
      });

      // JSON-LD: Breadcrumbs
      const crumbs: any[] = [{ '@type': 'ListItem', position: 1, name: 'Photographes', item: this.origin() + '/' }];
      if (hasCity) {
        crumbs.push({
          '@type': 'ListItem',
          position: 2,
          name: niceCity,
          item: `${this.origin()}/?city=${cForOg}`,
        });
      }
      if (hasTag) {
        crumbs.push({
          '@type': 'ListItem',
          position: hasCity ? 3 : 2,
          name: t,
          item: hasCity ? `${this.origin()}/?city=${cForOg}&tags=${t}` : `${this.origin()}/?tags=${t}`,
        });
      }
      this.upsertJsonLd('sf-breadcrumbs', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs,
      });

      // JSON-LD: Aggregate (optional)
      if (typeof opts?.resultsCount === 'number') {
        this.upsertJsonLd('sf-aggregate', {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          numberOfItems: opts.resultsCount,
          url: canonicalUrl,
        });
      }
    });
  }

  /**
   * SEO setup for the photographer landing page (/manage).
   */
  public setupSEOForManageLanding(): void {
    this.clearSeoTags();
    this.clearJsonLd([
      'sf-collection',
      'sf-breadcrumbs',
      'sf-aggregate',
      'sf-itemlist',
      'sf-article',
      'sf-article-breadcrumbs',
      'sf-manage-org',
      'sf-manage-webpage',
      'sf-manage-faq',
    ]);

    const origin = this.origin();
    const canonicalUrl = `${origin}/manage`;
    const lang = this.getActiveLang();
    const ogLocale = lang === 'fr' ? 'fr_FR' : 'en_US';
    const ogImage = `${origin}/assets/images/seo/manage/og-cover-${lang}.png`;

    const title$ = this.tOnce(
      'seo.manage.title',
      {},
      lang,
      lang === 'fr'
        ? 'ShutterFlow | Transformez vos visiteurs Instagram en clients'
        : 'ShutterFlow | Turn Instagram visitors into clients',
    );

    const desc$ = this.tOnce(
      'seo.manage.description',
      {},
      lang,
      lang === 'fr'
        ? 'Un lien de reservation ultra simple pour les photographes : vos clients bookent en 2 clics. Commission payee par le client.'
        : 'A simple booking link for photographers: clients book in 2 clicks. Commission paid by clients.',
    );

    const faqQ1$ = this.tOnce(
      'landing.faq.q1',
      {},
      lang,
      lang === 'fr' ? 'Qui paie la commission ?' : 'Who pays the commission?',
    );
    const faqA1$ = this.tOnce(
      'landing.faq.a1',
      {},
      lang,
      lang === 'fr'
        ? 'Les clients paient une commission de 12,5% sur chaque reservation.'
        : 'Clients pay a 12.5% commission on each booking.',
    );
    const faqQ2$ = this.tOnce(
      'landing.faq.q2',
      {},
      lang,
      lang === 'fr' ? 'Acompte ou paiement total ?' : 'Deposit or full payment?',
    );
    const faqA2$ = this.tOnce(
      'landing.faq.a2',
      {},
      lang,
      lang === 'fr'
        ? 'Vous choisissez pour chaque offre si le client paie un acompte ou le total.'
        : 'You choose per offer whether clients pay a deposit or the full amount.',
    );
    const faqQ3$ = this.tOnce(
      'landing.faq.q3',
      {},
      lang,
      lang === 'fr' ? 'Et si je veux un devis ?' : 'What if I need a custom quote?',
    );
    const faqA3$ = this.tOnce(
      'landing.faq.a3',
      {},
      lang,
      lang === 'fr'
        ? 'Les devis personnalises arrivent bientot. Pour l\'instant, redirigez vers votre Instagram ou email.'
        : 'Custom quotes are coming soon. For now, redirect to your Instagram or email.',
    );

    const faqQ4$ = this.tOnce(
      'landing.faq.q4',
      {},
      lang,
      lang === 'fr' ? 'La commission est-elle reduite ?' : 'Is there a reduced commission?',
    );
    const faqA4$ = this.tOnce(
      'landing.faq.a4',
      {},
      lang,
      lang === 'fr'
        ? 'Oui, le plan Pro la reduit a 5% pour 14,99 euros par mois.'
        : 'Yes, the Pro plan reduces it to 5% for €14.99 per month.',
    );

    forkJoin([title$, desc$, faqQ1$, faqA1$, faqQ2$, faqA2$, faqQ3$, faqA3$, faqQ4$, faqA4$])
      .pipe(take(1))
      .subscribe(([pageTitle, rawDesc, q1, a1, q2, a2, q3, a3, q4, a4]) => {
        let description = rawDesc.trim();
        if (description.length > 160) {
          description = `${description.slice(0, 157).trimEnd()}…`;
        }

        this.titleService.setTitle(pageTitle);
        this.metaService.updateTag({ name: 'description', content: description });
        this.metaService.updateTag({ name: 'robots', content: 'index,follow' });
        this.setCanonicalLink(canonicalUrl);

        this.metaService.updateTag({ property: 'og:type', content: 'website' });
        this.metaService.updateTag({ property: 'og:site_name', content: 'ShutterFlow' });
        this.metaService.updateTag({ property: 'og:locale', content: ogLocale });
        this.metaService.updateTag({ property: 'og:title', content: pageTitle });
        this.metaService.updateTag({ property: 'og:description', content: description });
        this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
        this.metaService.updateTag({ property: 'og:image', content: ogImage });
        this.metaService.updateTag({ property: 'og:image:width', content: '1200' });
        this.metaService.updateTag({ property: 'og:image:height', content: '630' });
        this.metaService.updateTag({ property: 'og:image:alt', content: pageTitle });

        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'twitter:title', content: pageTitle });
        this.metaService.updateTag({ name: 'twitter:description', content: description });
        this.metaService.updateTag({ name: 'twitter:image', content: ogImage });
        this.metaService.updateTag({ name: 'twitter:url', content: canonicalUrl });

        this.upsertJsonLd('sf-manage-org', {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'ShutterFlow',
          url: origin,
          logo: {
            '@type': 'ImageObject',
            url: `${origin}/assets/images/icon-512.png`,
          },
        });

        this.upsertJsonLd('sf-manage-webpage', {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: pageTitle,
          description,
          url: canonicalUrl,
          inLanguage: lang === 'fr' ? 'fr-FR' : 'en-US',
        });

        this.upsertJsonLd('sf-manage-faq', {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: q1,
              acceptedAnswer: { '@type': 'Answer', text: a1 },
            },
            {
              '@type': 'Question',
              name: q2,
              acceptedAnswer: { '@type': 'Answer', text: a2 },
            },
            {
              '@type': 'Question',
              name: q3,
              acceptedAnswer: { '@type': 'Answer', text: a3 },
            },
            {
              '@type': 'Question',
              name: q4,
              acceptedAnswer: { '@type': 'Answer', text: a4 },
            },
          ],
        });
      });
  }

  /**
   * SEO setup for the blog listing
   */
  public setupSEOForBlogList(payload: { articles: BlogArticle[]; total?: number }): void {
    this.clearSeoTags();
    this.metaService.removeTag('property="article:published_time"');
    this.metaService.removeTag('property="article:section"');
    this.metaService.removeTag('name="twitter:url"');
    this.clearJsonLd(['sf-collection', 'sf-breadcrumbs', 'sf-itemlist', 'sf-aggregate', 'sf-article', 'sf-article-breadcrumbs']);

    const { articles } = payload;
    const origin = this.origin();
    const canonicalUrl = `${origin}/blog`;

    const absolutize = (url?: string | null): string | null => {
      if (!url) return null;
      try {
        return new URL(url, origin).href;
      } catch {
        return null;
      }
    };
    const stripHtml = (value: string | null | undefined): string =>
      (value ?? '').replace(/<[^>]*>/g, ' ');
    const condense = (value: string): string =>
      value.replace(/\s+/g, ' ').trim();

    const featured = articles.find(article => !!article.cover);
    const ogImage = absolutize(featured?.cover) ?? `${origin}/assets/images/icon-512.png`;
    const ogImageAlt = featured?.title ?? 'ShutterFlow Blog';

    const lang = this.getActiveLang();
    const ogLocale = lang === 'fr' ? 'fr_FR' : 'en_US';

    const title$ = this.tOnce(
      'seo.blog.list.title',
      {},
      lang,
      lang === 'fr'
        ? 'Blog ShutterFlow – Inspirez vos séances photo'
        : 'ShutterFlow Blog – Book remarkable photographers with confidence',
    );

    const desc$ = this.tOnce(
      'seo.blog.list.description',
      {},
      lang,
      lang === 'fr'
        ? 'Guides pratiques pour choisir, réserver et gérer vos photographes professionnels.'
        : 'Practical playbooks to compare photographers, book with confidence, and manage shoots.',
    );

    forkJoin([title$, desc$]).pipe(take(1)).subscribe(([pageTitle, rawDesc]) => {
      let description = condense(stripHtml(rawDesc));
      if (description.length > 160) {
        description = `${description.slice(0, 157).trimEnd()}…`;
      }

      const keywords = Array.from(
        new Set(
          articles
            .flatMap(article => article.tags ?? [])
            .map(tag => tag.trim())
            .filter(Boolean),
        ),
      )
        .slice(0, 10)
        .join(', ');

      this.titleService.setTitle(pageTitle);
      this.setCanonicalLink(canonicalUrl);
      this.metaService.updateTag({ name: 'robots', content: 'index,follow' });
      this.metaService.updateTag({ name: 'description', content: description });
      if (keywords) {
        this.metaService.updateTag({ name: 'keywords', content: keywords });
      }

      this.metaService.updateTag({ property: 'og:type', content: 'website' });
      this.metaService.updateTag({ property: 'og:site_name', content: 'ShutterFlow' });
      this.metaService.updateTag({ property: 'og:locale', content: ogLocale });
      this.metaService.updateTag({ property: 'og:title', content: pageTitle });
      this.metaService.updateTag({ property: 'og:description', content: description });
      this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
      this.metaService.updateTag({ property: 'og:image', content: ogImage });
      this.metaService.updateTag({ property: 'og:image:alt', content: ogImageAlt });

      this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.metaService.updateTag({ name: 'twitter:title', content: pageTitle });
      this.metaService.updateTag({ name: 'twitter:description', content: description });
      this.metaService.updateTag({ name: 'twitter:image', content: ogImage });
      this.metaService.updateTag({ name: 'twitter:url', content: canonicalUrl });

      this.upsertJsonLd('sf-collection', {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: pageTitle,
        description,
        url: canonicalUrl,
        image: ogImage,
        publisher: {
          '@type': 'Organization',
          name: 'ShutterFlow',
          url: origin,
          logo: {
            '@type': 'ImageObject',
            url: `${origin}/assets/images/icon-512.png`,
          },
        },
        inLanguage: lang === 'fr' ? 'fr-FR' : 'en-US',
      });

      const listItems = articles.slice(0, 10).map((article, index) => {
        const itemUrl = `${origin}/blog/${article.slug}`;
        const parsedDate = article.published_at ? new Date(article.published_at) : null;
        const datePublished = parsedDate && !Number.isNaN(parsedDate.getTime())
          ? parsedDate.toISOString()
          : undefined;
        const headline = condense(stripHtml(article.title ?? ''));
        return {
          '@type': 'ListItem',
          position: index + 1,
          url: itemUrl,
          item: {
            '@type': 'BlogPosting',
            headline: headline || article.slug,
            url: itemUrl,
            datePublished,
          },
        };
      });

      this.upsertJsonLd('sf-itemlist', {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        url: canonicalUrl,
        numberOfItems: typeof payload.total === 'number' ? payload.total : articles.length,
        itemListElement: listItems,
      });

      this.upsertJsonLd('sf-breadcrumbs', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ShutterFlow',
            item: origin,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: canonicalUrl,
          },
        ],
      });
    });
  }

  /**
   * SEO setup for a single blog article.
   */
  public setupSEOForBlogArticle(article: BlogArticleDetail): void {
    this.clearSeoTags();
    this.metaService.removeTag('property="article:published_time"');
    this.metaService.removeTag('property="article:section"');
    this.metaService.removeTag('name="twitter:url"');
    this.clearJsonLd(['sf-article', 'sf-article-breadcrumbs']);

    const origin = this.origin();
    const absolutize = (url?: string | null): string | null => {
      if (!url) return null;
      try {
        return new URL(url, origin).href;
      } catch {
        return null;
      }
    };

    const baseTitle = (article.seoTitle ?? article.title ?? 'ShutterFlow').trim();
    const brand = 'ShutterFlow';
    const pageTitle = baseTitle.toLowerCase().includes(brand.toLowerCase())
      ? baseTitle
      : `${baseTitle} | ${brand}`;

    const stripHtml = (value: string | null | undefined): string =>
      (value ?? '').replace(/<[^>]*>/g, ' ');
    const condense = (value: string): string =>
      value.replace(/\s+/g, ' ').trim();

    let description = condense(
      stripHtml(article.seoDescription ?? article.excerpt ?? ''),
    );
    if (!description) {
      description = `Discover ${baseTitle} on ${brand}.`;
    }
    if (description.length > 160) {
      description = `${description.slice(0, 157).trimEnd()}…`;
    }

    const parseDate = (value?: string | null): string | null => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    };

    const canonicalUrl =
      absolutize(article.canonical_url) ?? `${origin}/blog/${article.slug}`;
    const coverUrl =
      absolutize(article.cover) ?? `${origin}/assets/images/icon-512.png`;
    const keywords = article.tags?.length ? article.tags.join(', ') : '';
    const lang = this.getActiveLang();
    const ogLocale = lang === 'fr' ? 'fr_FR' : 'en_US';
    const publishedTime = parseDate(article.published_at);
    const section = article.category
      ? article.category.charAt(0).toUpperCase() + article.category.slice(1)
      : null;

    this.titleService.setTitle(pageTitle);
    this.setCanonicalLink(canonicalUrl);
    this.metaService.updateTag({ name: 'robots', content: 'index,follow' });
    this.metaService.updateTag({ name: 'description', content: description });
    if (keywords) {
      this.metaService.updateTag({ name: 'keywords', content: keywords });
    }

    this.metaService.updateTag({ property: 'og:type', content: 'article' });
    this.metaService.updateTag({ property: 'og:locale', content: ogLocale });
    this.metaService.updateTag({ property: 'og:site_name', content: brand });
    this.metaService.updateTag({ property: 'og:title', content: baseTitle });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
    this.metaService.updateTag({ property: 'og:image', content: coverUrl });
    this.metaService.updateTag({ property: 'og:image:alt', content: baseTitle });

    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: baseTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    this.metaService.updateTag({ name: 'twitter:image', content: coverUrl });
    this.metaService.updateTag({ name: 'twitter:url', content: canonicalUrl });

    if (publishedTime) {
      this.metaService.updateTag({ property: 'article:published_time', content: publishedTime });
    }
    if (section) {
      this.metaService.updateTag({ property: 'article:section', content: section });
    }

    this.upsertJsonLd('sf-article', {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: baseTitle,
      description,
      url: canonicalUrl,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      image: coverUrl,
      datePublished: publishedTime ?? undefined,
      articleSection: section ?? undefined,
      keywords: keywords || undefined,
      publisher: {
        '@type': 'Organization',
        name: brand,
        url: origin,
        logo: {
          '@type': 'ImageObject',
          url: `${origin}/assets/images/icon-512.png`,
        },
      },
      isAccessibleForFree: true,
      inLanguage: lang === 'fr' ? 'fr-FR' : 'en-US',
    });

    this.upsertJsonLd('sf-article-breadcrumbs', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ShutterFlow',
          item: origin,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: `${origin}/blog`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: baseTitle,
          item: canonicalUrl,
        },
      ],
    });
  }

  /**
   * Minimal SEO for missing articles (noindex).
   */
  public markBlogArticleAsMissing(): void {
    this.clearSeoTags();
    this.clearJsonLd(['sf-article', 'sf-article-breadcrumbs']);

    const fallbackUrl = `${this.origin()}/blog`;
    const lang = this.getActiveLang();
    const title = lang === 'fr' ? 'Article introuvable | ShutterFlow' : 'Article not found | ShutterFlow';
    const description = lang === 'fr'
      ? 'Cet article n’est plus disponible.'
      : 'This article is no longer available.';
    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'robots', content: 'noindex,follow' });
    this.metaService.updateTag({ name: 'description', content: description });
    this.setCanonicalLink(fallbackUrl);
  }

  /**
   * SEO for a photographer profile page (async i18n to avoid raw keys).
   */
  public setupSEOForPhotographer(photographer: Photographer): void {
    this.clearSeoTags();
    this.clearJsonLd(['sf-collection', 'sf-breadcrumbs', 'sf-aggregate', 'sf-itemlist']);

    const origin = this.origin();
    const url = `${origin}/${photographer.slug}`;
    const absolutize = (value?: string | null): string | null => {
      if (!value) return null;
      try {
        return new URL(value, origin).href;
      } catch {
        return null;
      }
    };
    const image = absolutize(photographer.image) ?? `${origin}/assets/images/icon-512.png`;
    const firstname = photographer.firstname;
    const city = photographer.city ?? '';
    const keywords = [firstname, city, ...(photographer.services ?? []).map(s => s.name)]
      .filter(Boolean)
      .join(', ');

    const lang = this.getActiveLang();
    const title$ = this.tOnce('seo.photographer.title', { firstname, city }, lang, `${firstname} – Photographe ${city}`.trim());
    const desc$ = this.tOnce('seo.photographer.description', { firstname, city }, lang, `Réservez ${firstname}, photographe à ${city}.`);

    forkJoin([title$, desc$]).pipe(take(1)).subscribe(([title, description]) => {
      this.titleService.setTitle(title);
      this.metaService.updateTag({ name: 'description', content: description });

      this.setCanonicalLink(url);
      this.metaService.updateTag({ name: 'keywords', content: keywords });
      this.metaService.updateTag({ property: 'og:type', content: 'profile' });
      this.metaService.updateTag({ property: 'og:url', content: url });
      this.metaService.updateTag({ property: 'og:title', content: title });
      this.metaService.updateTag({ property: 'og:description', content: description });
      this.metaService.updateTag({ property: 'og:image', content: image });
      this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.metaService.updateTag({ name: 'twitter:title', content: title });
      this.metaService.updateTag({ name: 'twitter:description', content: description });
      this.metaService.updateTag({ name: 'twitter:image', content: image });
    });
  }

  /**
   * Homepage JSON-LD (one-time).
   */
  public injectStructuredDataForHomepage(document: Document): void {
    const id = 'shutterflow-structured-data';
    if (document.getElementById(id)) return;

    const lang = this.getActiveLang();
    this.tOnce('seo.marketplace.description', {}, lang, 'Comparez des photographes, voyez les prix et réservez en ligne.')
      .pipe(take(1))
      .subscribe(description => {
        const jsonLd = {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'ShutterFlow',
          url: 'https://shutterflow.io',
          description,
          publisher: {
            '@type': 'Organization',
            name: 'ShutterFlow',
            url: 'https://shutterflow.io',
            logo: { '@type': 'ImageObject', url: `${this.origin()}/assets/images/icon-512.png` },
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://shutterflow.io/?city={city}&tags={tags}',
            'query-input': ['optional name=city', 'optional name=tags'],
          },
        };
        const script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        script.text = JSON.stringify(jsonLd);
        document.head.appendChild(script);
      });
  }

  // ---------------- helpers ----------------

  /** Async translate once with fallback if key not found or not loaded yet. */
  private tOnce(key: string, params: any, lang: LangCode, fallback: string) {
    return this.transloco.selectTranslate(key, params, lang).pipe(
      take(1),
      map(val => (val === key || !val) ? fallback : val),
    );
  }

  /** Remove previously set meta tags to avoid stale values across navigations. */
  private clearSeoTags(
    tags: { name?: string; property?: string }[] = [
      { name: 'description' }, { name: 'keywords' }, { name: 'twitter:card' },
      { name: 'twitter:title' }, { name: 'twitter:description' }, { name: 'twitter:image' },
      { property: 'og:title' }, { property: 'og:description' }, { property: 'og:image' },
      { property: 'og:url' }, { property: 'og:type' }, { name: 'robots' },
      { property: 'og:locale' }, { property: 'og:image:width' },
      { property: 'og:image:height' }, { property: 'og:image:alt' },
      { property: 'og:site_name' },
    ],
  ): void {
    for (const tag of tags) {
      if (tag.name) this.metaService.removeTag(`name="${tag.name}"`);
      if (tag.property) this.metaService.removeTag(`property="${tag.property}"`);
    }
  }

  /** Remove JSON-LD script tags by id(s). */
  private clearJsonLd(ids: string[]): void {
    ids.forEach(id => { const el = this.doc.getElementById(id); if (el) el.remove(); });
  }

  /** Create/replace a canonical link tag. */
  private setCanonicalLink(url: string) {
    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /** Inject/replace a JSON-LD script tag by id. */
  public upsertJsonLd(id: string, json: object) {
    let el = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = this.doc.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      this.doc.head.appendChild(el);
    }
    el.text = JSON.stringify(json);
  }

  /** Build canonical URL keeping normalized query param order. */
  private buildCanonicalWithParams(city: CityKey | null, tags: string[] | null): string {
    const base = this.origin() + '/';
    const sp = new URLSearchParams();
    if (city) sp.set('city', city);
    if (tags && tags.length) sp.set('tags', tags.join(','));
    const q = sp.toString();
    return q ? `${base}?${q}` : base;
  }

  /** Get active language from Transloco (normalized to 'fr' | 'en'). */
  private getActiveLang(): LangCode {
    const a = this.transloco.getActiveLang() || 'en';
    return a.startsWith('fr') ? 'fr' : 'en';
  }

  /** Choose OG image based on city/lang; fallback to generic per lang. */
  private buildOgCityImage(city: CityKey | null, lang: LangCode): string {
    const base = `${this.origin()}/assets/images/seo/marketplace`;
    if (city) return `${base}/cities/og-${city}.${lang}.png`;
    return `${base}/og-cover-${lang}.png`;
  }

  /** SSR-safe origin detection. */
  private origin(): string {
    return isPlatformBrowser(this.platformId) ? window.location.origin : 'https://shutterflow.io';
  }

  /** Fallback to read city/tags from current URL if component props aren't ready yet. */
  private readUrlParamsFallback(): { city: string | null; tags: string[] } {
    if (!isPlatformBrowser(this.platformId)) return { city: null, tags: [] };
    try {
      const u = new URL(window.location.href);
      const city = u.searchParams.get('city');
      const tagsParam = u.searchParams.get('tags');
      const tags = tagsParam ? tagsParam.split(',').map(s => s.trim()).filter(Boolean) : [];
      return { city, tags };
    } catch {
      return { city: null, tags: [] };
    }
  }
}
